import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ALL_REPORT_LANGUAGES,
  REPORT_TEMPLATE_STATUS,
  type ReportLanguage,
} from '@lims/shared-types';
import { updateReportDraftSchema } from '@lims/shared-validation';
import {
  reportTemplatesApi,
  reportsApi,
  type EligibleResultView,
  type ReportDetailView,
  type ReportTemplateView,
} from '../../api/endpoints.js';
import { ErrorNotice } from '../../components/ErrorNotice.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  Dialog,
  SelectField,
  TextAreaField,
  useToast,
} from '../../components/ui/index.js';
import { currentLocale } from '../../i18n/index.js';
import { TEMPLATES_QUERY_KEY } from '../settings/report-template-parts.js';
import { ResultSelection } from './ResultSelection.js';
import {
  REPORTS_QUERY_KEY,
  ReportRefusalNotice,
  bilingual,
  hasRefusal,
  openPdf,
  requirePdf,
  saveBlob,
} from './report-parts.js';

/**
 * The acts on a report's draft, up to and including issuance — and the staff
 * download of an issued version.
 *
 * ===========================================================================
 * NOTHING HERE DELIVERS ANYTHING (CLAUDE.md section 8, D121, D129).
 * ===========================================================================
 *
 * Issuing numbers the report, renders its PDF ONCE and stores it; the dialog
 * says so before anybody presses it, and says that it sends nothing. A staff
 * download serves those stored bytes and is recorded as a download, never as a
 * delivery or a receipt.
 *
 * Every control is offered from the `can.*` hints the server computed for the
 * caller, and every endpoint re-checks the permission and the state. Hiding a
 * button is presentation; the server is the authority (section 5.1).
 */

/** Reasons shorter than this are refused by the shared `reasonSchema`. */
const MINIMUM_REASON = 3;
/** The shared schema's limit on remarks. */
const MAXIMUM_REMARKS = 4000;

// ---------------------------------------------------------------------------
// Templates and language
// ---------------------------------------------------------------------------

/**
 * The laboratory's ACTIVE templates.
 *
 * Asked of the server whatever the caller holds: whether a preparer may read
 * them is the server's decision, and a refusal is shown as a refusal rather
 * than as "no template exists".
 */
export function useActiveTemplates() {
  return useQuery({
    queryKey: [...TEMPLATES_QUERY_KEY, 'list', { status: REPORT_TEMPLATE_STATUS.ACTIVE, page: 1 }],
    queryFn: () =>
      reportTemplatesApi.list({ status: REPORT_TEMPLATE_STATUS.ACTIVE, pageSize: 100 }),
  });
}

/** The languages a template allows, in the product's fixed order. */
export function templateLanguages(template: ReportTemplateView | undefined): ReportLanguage[] {
  if (!template) return [];
  return ALL_REPORT_LANGUAGES.filter((language) => template.languages.includes(language));
}

/** A language the template supports: the one held if still allowed, else the reader's, else the first. */
export function fittingLanguage(
  template: ReportTemplateView | undefined,
  held: ReportLanguage | null,
): ReportLanguage | null {
  const allowed = templateLanguages(template);
  if (held && allowed.includes(held)) return held;
  const reader = currentLocale();
  if (allowed.includes(reader)) return reader;
  return allowed[0] ?? null;
}

export function TemplateLanguageFields({
  templates,
  templateId,
  language,
  onTemplateChange,
  onLanguageChange,
  showErrors,
}: {
  readonly templates: readonly ReportTemplateView[];
  readonly templateId: string | null;
  readonly language: ReportLanguage | null;
  readonly onTemplateChange: (templateId: string | null) => void;
  readonly onLanguageChange: (language: ReportLanguage | null) => void;
  readonly showErrors: boolean;
}) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const template = templates.find((candidate) => candidate.id === templateId);

  return (
    <div className="grid gap-stack sm:grid-cols-2">
      <SelectField
        label={t('reports.prepare.template')}
        description={t('reports.prepare.templateHint')}
        placeholder={t('common.notSet')}
        value={template ? template.id : null}
        onChange={(value) => {
          onTemplateChange(value);
          const next = templates.find((candidate) => candidate.id === value);
          onLanguageChange(fittingLanguage(next, language));
        }}
        isRequired
        options={templates.map((candidate) => ({
          value: candidate.id,
          label: bilingual(candidate.name, locale) ?? t('common.notRecorded'),
        }))}
        {...(showErrors && !template ? { error: t('validation.required') } : {})}
      />
      <SelectField
        label={t('reports.prepare.language')}
        description={t('reports.prepare.languageHint')}
        placeholder={t('common.notSet')}
        value={language}
        onChange={(value) => onLanguageChange((value as ReportLanguage | null) ?? null)}
        isRequired
        isDisabled={!template}
        options={templateLanguages(template).map((value) => ({
          value,
          label: t(`reportLanguage.${value}`),
        }))}
        {...(showErrors && template && !language ? { error: t('validation.required') } : {})}
      />
    </div>
  );
}

/** "Test · sample" for a result a refusal names. */
export function attemptDescriber(
  results: readonly {
    readonly attemptId: string;
    readonly test: { readonly name: { readonly ar: string | null; readonly en: string | null } };
    readonly sample: { readonly number: string };
  }[],
): (attemptId: string) => ReactNode | null {
  const locale = currentLocale();
  return (attemptId) => {
    const result = results.find((candidate) => candidate.attemptId === attemptId);
    if (!result) return null;
    return (
      <>
        <bdi>{bilingual(result.test.name, locale) ?? ''}</bdi>
        {' · '}
        <span className="measure" dir="ltr">
          {result.sample.number}
        </span>
      </>
    );
  };
}

// ---------------------------------------------------------------------------
// Edit the draft
// ---------------------------------------------------------------------------

export function EditDraftDialog({
  report,
  onClose,
}: {
  readonly report: ReportDetailView;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const { show } = useToast();
  const queryClient = useQueryClient();
  const draft = report.draftVersion;

  const [attemptIds, setAttemptIds] = useState<string[]>(
    draft
      ? [...draft.results]
          .sort((first, second) => first.sortOrder - second.sortOrder)
          .map((result) => result.attemptId)
      : [],
  );
  const [templateId, setTemplateId] = useState<string | null>(draft?.template.id ?? null);
  const [language, setLanguage] = useState<ReportLanguage | null>(draft?.language ?? null);
  const [remarks, setRemarks] = useState(draft?.remarks ?? '');
  const [showErrors, setShowErrors] = useState(false);

  const eligible = useQuery({
    queryKey: [...REPORTS_QUERY_KEY, 'eligible', report.workOrder.id],
    queryFn: () => reportsApi.eligibleResults(report.workOrder.id),
  });
  const templates = useActiveTemplates();
  const activeTemplates = templates.data?.data ?? [];

  const save = useMutation({
    mutationFn: (body: Parameters<typeof reportsApi.updateDraft>[1]) =>
      reportsApi.updateDraft(report.id, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: REPORTS_QUERY_KEY });
      // Raised from the server's answer, never from the button press.
      show({ tone: 'success', message: t('reports.detail.draftSaved') });
      onClose();
    },
    onError: async (error) => {
      // Somebody changed the draft meanwhile: reload what is really there.
      if (hasRefusal(error, 'draft_changed')) {
        await queryClient.invalidateQueries({ queryKey: REPORTS_QUERY_KEY });
      }
    },
  });

  if (!draft) return null;

  const trimmedRemarks = remarks.trim();
  const remarksError = trimmedRemarks.length > MAXIMUM_REMARKS ? t('validation.tooLong') : undefined;
  const selectionError = attemptIds.length === 0 ? t('validation.required') : undefined;
  const template = activeTemplates.find((candidate) => candidate.id === templateId);
  const languageOk = template && language && template.languages.includes(language);

  const submit = () => {
    setShowErrors(true);
    if (selectionError || remarksError || !template || !languageOk || !language) return;
    const body = {
      draftRevision: draft.draftRevision,
      templateId: template.id,
      language,
      attemptIds,
      remarks: trimmedRemarks === '' ? null : trimmedRemarks,
    };
    // The schema the API validates with. A body it refuses is never sent.
    if (!updateReportDraftSchema.safeParse(body).success) return;
    save.mutate(body);
  };

  const eligibleRows: readonly EligibleResultView[] = eligible.data?.data ?? [];

  return (
    <Dialog
      isOpen
      onClose={onClose}
      size="xl"
      title={t('reports.detail.editDraft')}
      footer={
        <ActionBar>
          <ActionButton priority="tertiary" onPress={onClose} isDisabled={save.isPending}>
            {t('common.cancel')}
          </ActionButton>
          <ActionButton priority="primary" isLoading={save.isPending} onPress={submit}>
            {t('reports.detail.saveDraft')}
          </ActionButton>
        </ActionBar>
      }
    >
      <div className="flex flex-col gap-stack">
        {eligible.isLoading || templates.isLoading ? (
          <p className="text-body text-default-600">{t('common.loading')}</p>
        ) : null}
        {eligible.error ? <ErrorNotice error={eligible.error} /> : null}
        {templates.error ? <ErrorNotice error={templates.error} /> : null}
        {eligible.isSuccess ? (
          <ResultSelection
            eligible={eligibleRows}
            selected={attemptIds}
            onChange={setAttemptIds}
            currentReportId={report.id}
            {...(showErrors && selectionError ? { error: selectionError } : {})}
          />
        ) : null}
        {templates.isSuccess ? (
          <TemplateLanguageFields
            templates={activeTemplates}
            templateId={templateId}
            language={language}
            onTemplateChange={setTemplateId}
            onLanguageChange={setLanguage}
            showErrors={showErrors}
          />
        ) : null}
        <TextAreaField
          label={t('reports.prepare.remarks')}
          description={t('reports.prepare.remarksHint')}
          value={remarks}
          onChange={setRemarks}
          rows={3}
          {...(remarksError ? { error: remarksError } : {})}
        />
        {save.error ? (
          <ReportRefusalNotice
            error={save.error}
            describeAttempt={attemptDescriber(eligibleRows)}
          />
        ) : null}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Issue
// ---------------------------------------------------------------------------

export function IssueDialog({
  report,
  draftRevision,
  onClose,
}: {
  readonly report: ReportDetailView;
  /** The revision the issuer previewed, or the one in front of them. */
  readonly draftRevision: number;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const { show } = useToast();
  const queryClient = useQueryClient();

  const issue = useMutation({
    mutationFn: () => reportsApi.issue(report.id, { draftRevision }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: REPORTS_QUERY_KEY });
      // Issued — and, said in the same breath, not delivered.
      show({ tone: 'success', message: t('reports.detail.issued') });
      onClose();
    },
    onError: async (error) => {
      if (hasRefusal(error, 'draft_changed')) {
        await queryClient.invalidateQueries({ queryKey: REPORTS_QUERY_KEY });
      }
    },
  });

  return (
    <Dialog
      isOpen
      onClose={onClose}
      size="lg"
      title={t('reports.detail.issueAction')}
      footer={
        <ActionBar>
          <ActionButton priority="tertiary" onPress={onClose} isDisabled={issue.isPending}>
            {t('common.cancel')}
          </ActionButton>
          <ActionButton
            priority="primary"
            isLoading={issue.isPending}
            onPress={() => issue.mutate()}
          >
            {t('reports.detail.issueAction')}
          </ActionButton>
        </ActionBar>
      }
    >
      <div className="flex flex-col gap-stack">
        <Alert
          tone="warning"
          title={t('reports.detail.issueWarningTitle')}
          description={t('reports.detail.issueWarningBody')}
        />
        <Alert
          tone="info"
          title={t('reports.issuanceNotDeliveryTitle')}
          description={t('reports.issuanceNotDeliveryBody')}
        />
        <p className="text-body text-default-700">{t('reports.detail.issueWhatYouPreviewed')}</p>
        {issue.isPending ? (
          <p className="text-caption text-default-600" aria-live="polite">
            {t('reports.detail.issuing')}
          </p>
        ) : null}
        {issue.error ? (
          <ReportRefusalNotice
            error={issue.error}
            describeAttempt={attemptDescriber(report.draftVersion?.results ?? [])}
          />
        ) : null}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Discard
// ---------------------------------------------------------------------------

export function DiscardDraftDialog({
  report,
  onClose,
}: {
  readonly report: ReportDetailView;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const { show } = useToast();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const reasonReady = reason.trim().length >= MINIMUM_REASON;

  const discard = useMutation({
    mutationFn: () => reportsApi.discard(report.id, { reason: reason.trim() }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: REPORTS_QUERY_KEY });
      show({ tone: 'success', message: t('reports.detail.discarded') });
      onClose();
    },
  });

  return (
    <Dialog
      isOpen
      onClose={onClose}
      title={t('reports.detail.discardAction')}
      footer={
        <ActionBar>
          <ActionButton priority="tertiary" onPress={onClose} isDisabled={discard.isPending}>
            {t('common.cancel')}
          </ActionButton>
          <ActionButton
            priority="destructive"
            // Mandatory here AND on the server, which refuses a discard without one.
            isDisabled={!reasonReady}
            isLoading={discard.isPending}
            onPress={() => discard.mutate()}
          >
            {t('reports.detail.discardAction')}
          </ActionButton>
        </ActionBar>
      }
    >
      <div className="flex flex-col gap-stack">
        <Alert
          tone="warning"
          title={t('reports.detail.discardWarningTitle')}
          description={t('reports.detail.discardWarningBody')}
        />
        <p className="text-caption text-default-600">
          {t('reports.detail.discardOnlyNeverIssued')}
        </p>
        <TextAreaField
          label={t('reports.detail.discardReason')}
          value={reason}
          onChange={(value) => {
            setReason(value);
            setTouched(true);
          }}
          isRequired
          rows={3}
          {...(touched && !reasonReady
            ? {
                error:
                  reason.trim() === '' ? t('validation.required') : t('validation.tooShort'),
              }
            : {})}
        />
        {discard.error ? <ReportRefusalNotice error={discard.error} /> : null}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// The draft's actions
// ---------------------------------------------------------------------------

/**
 * Edit, preview, issue and discard — each offered only where the server's hint
 * says the caller may, and each re-checked by the server.
 */
export function DraftActions({ report }: { readonly report: ReportDetailView }) {
  const { t } = useTranslation();
  const [dialog, setDialog] = useState<'edit' | 'issue' | 'discard' | null>(null);
  /**
   * The revision last previewed, so issuance names exactly what was looked at.
   * If the draft changed after that preview — even by this person — the server
   * refuses with `draft_changed`, and the issuer previews again.
   */
  const [previewedRevision, setPreviewedRevision] = useState<number | null>(null);
  const draft = report.draftVersion;

  const preview = useMutation({
    mutationFn: async (revision: number) => {
      openPdf(requirePdf(await reportsApi.previewDraft(report.id)));
      return revision;
    },
    onSuccess: (revision) => setPreviewedRevision(revision),
  });

  if (!draft) return null;
  const { can } = report;
  if (!can.editDraft && !can.preview && !can.issue && !can.discard) return null;

  return (
    <div className="flex flex-col gap-stack">
      <div className="flex flex-wrap items-center gap-inline">
        {can.editDraft ? (
          <ActionButton priority="secondary" size="sm" onPress={() => setDialog('edit')}>
            {t('reports.detail.editDraft')}
          </ActionButton>
        ) : null}
        {can.preview ? (
          <ActionButton
            priority="secondary"
            size="sm"
            isLoading={preview.isPending}
            onPress={() => preview.mutate(draft.draftRevision)}
          >
            {t('reports.detail.previewAction')}
          </ActionButton>
        ) : null}
        {can.issue ? (
          <ActionButton priority="primary" size="sm" onPress={() => setDialog('issue')}>
            {t('reports.detail.issueAction')}
          </ActionButton>
        ) : null}
        {can.discard ? (
          <ActionButton priority="destructive" size="sm" onPress={() => setDialog('discard')}>
            {t('reports.detail.discardAction')}
          </ActionButton>
        ) : null}
      </div>
      {can.preview ? (
        <p className="text-caption text-default-600">{t('reports.detail.previewHint')}</p>
      ) : null}
      {preview.error ? <ReportRefusalNotice error={preview.error} /> : null}

      {dialog === 'edit' ? (
        <EditDraftDialog report={report} onClose={() => setDialog(null)} />
      ) : null}
      {dialog === 'issue' ? (
        <IssueDialog
          report={report}
          draftRevision={previewedRevision ?? draft.draftRevision}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog === 'discard' ? (
        <DiscardDraftDialog report={report} onClose={() => setDialog(null)} />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Staff download of an issued version
// ---------------------------------------------------------------------------

/**
 * The issued version's PDF in Arabic and in English (D330). A staff download —
 * recorded as such, and never a delivery or a receipt (D129).
 *
 * EACH BUTTON NAMES ITS LANGUAGE, and the request names it too: the interface
 * language translates the buttons and never decides which PDF is produced.
 * Both come from the same issued version — same number, same version.
 */
export function VersionFileDownload({
  report,
  versionNumber,
  languages,
}: {
  readonly report: ReportDetailView;
  readonly versionNumber: number;
  /** The languages the server offers for this version. */
  readonly languages: readonly ReportLanguage[];
}) {
  const { t } = useTranslation();
  const download = useMutation({
    mutationFn: async (language: ReportLanguage) => {
      const blob = requirePdf(
        await reportsApi.downloadVersionFile(report.id, versionNumber, language),
      );
      const base = report.reportNumber ?? report.id;
      saveBlob(blob, `${base}-v${versionNumber}-${language}.pdf`);
    },
  });
  const offered = ALL_REPORT_LANGUAGES.filter((language) => languages.includes(language));
  if (offered.length === 0) return null;

  return (
    <div className="flex flex-col items-start gap-inline">
      <div className="flex flex-wrap items-center gap-inline">
        {offered.map((language) => (
          <ActionButton
            key={language}
            priority="secondary"
            size="sm"
            isLoading={download.isPending && download.variables === language}
            isDisabled={download.isPending && download.variables !== language}
            onPress={() => download.mutate(language)}
          >
            {t(`reports.detail.downloadIn.${language}`)}
          </ActionButton>
        ))}
      </div>
      {download.isPending ? (
        <p className="text-caption text-default-600" aria-live="polite">
          {t('reports.detail.downloading')}
        </p>
      ) : null}
      {download.error ? <ReportRefusalNotice error={download.error} /> : null}
    </div>
  );
}
